import { QuizType } from "@/redux/reducers/quizzes/quizSlice";
import React, { useCallback, useMemo } from "react";
import { FormQuiz } from "../FormQuiz";
import { Navigate, useParams } from "react-router-dom";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { ErrorBlock } from "@/components/ErrorBlock";
import { useQuery } from "react-query";
import { getLibraryQuizV2 } from "@/features/Quiz/api/libraryQuizDetailV2";
import type { LibraryQuizDetailsV2 } from "@/features/Quiz/api/libraryQuizDetailV2";
import {
  getAdminQuizById,
  getAdminQuizDetails,
  getQuiz,
  getQuizLibraryById,
  getUserQuizById,
  getUserQuizDetails,
} from "@/features/Quiz/api/getQuizzes";
import { useAuth } from "@/context/AuthContext";
import { mapAdminQuizDetailsToQuiz } from "@/features/Quiz/utils/quizV2AdminEdit";
import type { AdminQuizDetailsContent } from "@/features/Quiz/utils/quizV2AdminEdit";
import { isQuizUuid, libraryQuizDetailsToAdminContent, getLibraryQuizV2QueryKey } from "@/features/Quiz/utils/quizV2Library";

type Props = {
  isLibrary?: boolean;
  isAdminQuiz?: boolean;
  /** Edit/create via quiz-svc (GET details / PUT update by UUID). */
  isAdminQuizV2?: boolean;
  /** Super-admin edit of instructor quiz (GET userQuiz / PUT update/userQuiz). */
  isCustomerQuizV2?: boolean;
  /** Owner edit of their own quiz-svc quiz (My Quizzes / Shared By Me); same uuid data path, no super-admin gate. */
  isMyQuizV2?: boolean;
  isUserQuiz?: boolean;
  /** Library quiz CRUD via quiz-svc V2 APIs when OpenSearch library flag is on. */
  libraryQuizV2Api?: boolean;
};

export const EditQuiz = ({
  isLibrary = false,
  isAdminQuiz = false,
  isAdminQuizV2 = false,
  isCustomerQuizV2 = false,
  isMyQuizV2 = false,
  isUserQuiz = false,
  libraryQuizV2Api = false,
}: Props) => {
  const { user } = useAuth();
  const isSuperAdmin = user?.roles.includes("ROLE_SUPER_ADMIN");

  // Both modes edit a quiz-svc user quiz by UUID; they differ only in the access gate below.
  const isUserQuizV2 = isCustomerQuizV2 || isMyQuizV2;

  const { quizId, quizLibraryId, adminQuizId, userQuizId, customerQuizId, quizUuid } =
    useParams();

  // Detect UUID by shape so we never Number(uuid) → NaN; gate V2 fetch on the flag.
  const routeLibraryUuid = [quizLibraryId, quizId].find(isQuizUuid);
  const libraryV2Uuid = libraryQuizV2Api ? routeLibraryUuid : undefined;
  const isLibraryQuizV2 = !!libraryV2Uuid;
  const effectiveIsLibrary = isLibrary || isLibraryQuizV2;

  const quizQueryKey = useCallback(() => {
    if (isUserQuizV2 && quizUuid) {
      return `getUserQuizDetails-${quizUuid}`;
    }
    if (isAdminQuizV2 && quizUuid) {
      return `getAdminQuizDetails-${quizUuid}`;
    }
    if (userQuizId) {
      return `getUserQuiz-${userQuizId}`;
    }
    if (adminQuizId) {
      return `getAdminQuiz-${adminQuizId}`;
    }
    if (libraryV2Uuid) {
      return getLibraryQuizV2QueryKey(libraryV2Uuid);
    }
    if (quizLibraryId) {
      return `getQuizLibrary-${quizLibraryId}`;
    }
    if (customerQuizId) {
      return `getQuizLibrary-${customerQuizId}`;
    }
    return `getQuiz-${quizId}`;
  }, [
    adminQuizId,
    quizLibraryId,
    quizId,
    customerQuizId,
    isAdminQuizV2,
    isUserQuizV2,
    quizUuid,
    libraryV2Uuid,
    userQuizId,
  ]);

  const { data, isLoading, isError, error } = useQuery(
    quizQueryKey(),
    async () => {
      if (isUserQuizV2 && quizUuid) {
        return await getUserQuizDetails(quizUuid);
      }
      if (isAdminQuizV2 && quizUuid) {
        return await getAdminQuizDetails(quizUuid);
      }
      if (quizId || quizLibraryId || adminQuizId || userQuizId) {
        if (userQuizId) {
          return await getUserQuizById(Number(userQuizId));
        }
        if (adminQuizId) {
          return await getAdminQuizById(Number(adminQuizId));
        }
        if (libraryV2Uuid) {
          return await getLibraryQuizV2(libraryV2Uuid);
        }
        if (quizLibraryId && !isQuizUuid(quizLibraryId)) {
          return await getQuizLibraryById(Number(quizLibraryId));
        }
        if (customerQuizId) {
          return await getAdminQuizById(Number(customerQuizId));
        }
        if (quizId && !isQuizUuid(quizId)) {
          return await getQuiz(Number(quizId));
        }
        return null;
      }

      return null;
    },
    {
      enabled: !(routeLibraryUuid && !libraryQuizV2Api),
      useErrorBoundary: (error: any) => error.response?.status >= 400,
    }
  );

  const mappedQuiz = useMemo(() => {
    if ((isAdminQuizV2 || isUserQuizV2) && data) {
      return mapAdminQuizDetailsToQuiz(data as AdminQuizDetailsContent);
    }
    if (isLibraryQuizV2 && data) {
      return mapAdminQuizDetailsToQuiz(
        libraryQuizDetailsToAdminContent(data as LibraryQuizDetailsV2)
      );
    }
    return (data as QuizType) ?? null;
  }, [isAdminQuizV2, isUserQuizV2, isLibraryQuizV2, data]);

  const adminQuizV2Details =
    isAdminQuizV2 || isUserQuizV2 ? (data as AdminQuizDetailsContent | null) ?? null : null;

  const libraryQuizV2Details = isLibraryQuizV2
    ? (data as LibraryQuizDetailsV2 | null) ?? null
    : null;

  // Super-admin gate applies to the Customer Quizzes V2 flow only; owners may edit their own quiz (isMyQuizV2).
  if (isCustomerQuizV2 && !isSuperAdmin) {
    return <Navigate to="/" />;
  }

  if (!isSuperAdmin && !isUserQuiz && !effectiveIsLibrary && !isAdminQuiz && !isMyQuizV2) {
    return <Navigate to="/" />;
  }

  // UUID in the URL but V2 library API is off — never call Number(uuid)
  if (routeLibraryUuid && !libraryQuizV2Api) {
    return <ErrorBlock error={"Error"} textOnly={false} />;
  }

  if (isLoading || (!isError && !data)) return <LoadingSpinner isFullScreen />;
  if (error?.statusCode === 404 || isError)
    return <ErrorBlock error={"Error"} textOnly={false} />;

  return (
    <FormQuiz
      quiz={mappedQuiz ?? null}
      adminQuizV2Details={adminQuizV2Details}
      libraryQuizV2Details={libraryQuizV2Details}
      isLibrary={effectiveIsLibrary}
      isLibraryQuizV2={isLibraryQuizV2}
      isUserQuiz={isUserQuiz}
      isAdminQuiz={isAdminQuiz}
      isAdminQuizV2={isAdminQuizV2}
      isCustomerQuizV2={isUserQuizV2}
      isEdit
    />
  );
};
